// templateSync.js implementa la traducción del diff entre la definición
// conocida del servidor (serverDefinition) y el borrador local del builder
// (localDefinition) a una secuencia ordenada de operaciones estructurales
// aceptadas por PUT /templates/:id (dto/terrain/template_ops.go).
//
// El backend siempre asigna sectionId/questionId nuevos al crear
// secciones/preguntas: por eso la comparación usa como clave el
// identificador de SERVIDOR ya conocido para cada sección/pregunta local
// (a través de un mapa localId -> serverId mantenido por el store, ver
// store/modules/terrain.js). Las secciones/preguntas locales que aún no
// tienen id de servidor se tratan siempre como "addSection"/"addQuestion"
// pendientes, sin importar si el usuario las creó hace mucho o recién.
//
// Las configuraciones profundas se persisten con updateQuestion. Las
// condiciones se difieren hasta que todos los IDs locales tengan su par
// asignado por backend; ver diffConditionOps.
//
// Requirements: 2.1, 2.2, 2.6, 2.7 (spec: levantamiento-terreno)

/**
 * Resolves the server id for a local section/question, given a map of
 * localId -> serverId. Returns null when the entity has no server id yet
 * (i.e. it was created locally and never persisted).
 */
function resolveServerId(localId, idMap) {
  if (!idMap) return null;
  return idMap[localId] || null;
}

/**
 * Compares the ordered list of local sections against the server sections
 * and returns the structural ops required to reconcile them, in a safe
 * order: deletes last is wrong for reorder validation (reorder requires the
 * exact existing id set), so the order applied here is:
 *   1) renames/describes for sections that still exist
 *   2) additions for local sections without a server id
 *   3) deletions for server sections no longer present locally
 *   4) reorder (only when the surviving id set order actually changed)
 * Additions are emitted before deletions/reorder so that a newly created
 * section can immediately receive question ops in the same pass; deletions
 * happen after in case a section was duplicated then the original removed.
 *
 * @param {object} serverDefinition {title, description, sections:[{sectionId, title, description, questions:[...]}]}
 * @param {object} localDefinition {title, description, sections:[{sectionId (local), title, description, questions:[...]}]}
 * @param {object} sectionIdMap localSectionId -> serverSectionId
 * @param {object} questionIdMap localQuestionId -> serverQuestionId
 * @returns {Array<object>} ops, each missing `expectedVersion` (caller fills it in sequentially)
 */
export function diffDefinitionsToOps(serverDefinition, localDefinition, sectionIdMap = {}, questionIdMap = {}) {
  const ops = [];
  const server = serverDefinition || { title: '', description: '', sections: [] };
  const local = localDefinition || { title: '', description: '', sections: [] };
  const serverSections = server.sections || [];
  const localSections = local.sections || [];

  // 1) Template-level metadata (setMeta).
  if ((local.title || '') !== (server.title || '') || (local.description || '') !== (server.description || '')) {
    ops.push({ op: 'setMeta', title: local.title || '', description: local.description || '' });
  }

  const serverSectionById = new Map(serverSections.map((s) => [s.sectionId, s]));
  const localSectionsWithServerId = [];
  const localSectionsWithoutServerId = [];

  for (const localSection of localSections) {
    const serverSectionId = resolveServerId(localSection.sectionId, sectionIdMap);
    if (serverSectionId && serverSectionById.has(serverSectionId)) {
      localSectionsWithServerId.push({ localSection, serverSectionId });
    } else {
      localSectionsWithoutServerId.push(localSection);
    }
  }

  const survivingServerSectionIds = new Set(localSectionsWithServerId.map((entry) => entry.serverSectionId));

  // 2) Renames/describes on sections that still exist server-side.
  for (const { localSection, serverSectionId } of localSectionsWithServerId) {
    const serverSection = serverSectionById.get(serverSectionId);
    if ((localSection.title || '') !== (serverSection.title || '')) {
      ops.push({ op: 'renameSection', sectionId: serverSectionId, title: localSection.title || '' });
    }
    if ((localSection.description || '') !== (serverSection.description || '')) {
      ops.push({ op: 'describeSection', sectionId: serverSectionId, description: localSection.description || '' });
    }
    ops.push(...diffQuestions(serverSection, localSection, questionIdMap));
  }

  // 3) New sections (no server id yet). Their questions are all "new" too,
  // since a brand-new section cannot already contain server-known
  // questions.
  for (const localSection of localSectionsWithoutServerId) {
    ops.push({
      op: 'addSection',
      title: localSection.title || '',
      description: localSection.description || '',
      __localSectionId: localSection.sectionId,
      __pendingQuestions: localSection.questions || [],
    });
  }

  // 4) Deletions: server sections no longer present locally.
  for (const serverSection of [...serverSections].reverse()) {
    if (!survivingServerSectionIds.has(serverSection.sectionId)) {
      ops.push({ op: 'deleteSection', sectionId: serverSection.sectionId });
    }
  }

  // 5) Reorder: only when the surviving id set order changed and no
  // pending additions/deletions are outstanding for sections (reorder
  // requires the exact same id set already present server-side, so it is
  // only meaningful once every locally-known section maps to a server id
  // and no server section is missing locally).
  if (localSectionsWithoutServerId.length === 0 && survivingServerSectionIds.size === serverSections.length) {
    const desiredOrder = localSectionsWithServerId.map((entry) => entry.serverSectionId);
    const currentOrder = serverSections.map((s) => s.sectionId);
    if (desiredOrder.length === currentOrder.length && desiredOrder.some((id, index) => id !== currentOrder[index])) {
      ops.push({ op: 'reorderSections', sectionOrder: desiredOrder });
    }
  }

  return ops;
}

/**
 * Same reconciliation logic as diffDefinitionsToOps but scoped to the
 * questions of a single section. Returns ops with `sectionId` already set
 * to the server section id (the caller of diffDefinitionsToOps only calls
 * this for sections that already have a server id).
 */
function diffQuestions(serverSection, localSection, questionIdMap) {
  const ops = [];
  const serverQuestions = serverSection.questions || [];
  const localQuestions = localSection.questions || [];
  const sectionId = serverSection.sectionId;

  const serverQuestionById = new Map(serverQuestions.map((q) => [q.questionId, q]));
  const localWithServerId = [];
  const localWithoutServerId = [];

  for (const localQuestion of localQuestions) {
    const serverQuestionId = resolveServerId(localQuestion.questionId, questionIdMap);
    if (serverQuestionId && serverQuestionById.has(serverQuestionId)) {
      localWithServerId.push({ localQuestion, serverQuestionId });
    } else {
      localWithoutServerId.push(localQuestion);
    }
  }

  const survivingServerQuestionIds = new Set(localWithServerId.map((entry) => entry.serverQuestionId));

  for (const { localQuestion, serverQuestionId } of localWithServerId) {
    const serverQuestion = serverQuestionById.get(serverQuestionId);
    const translated = translateQuestion(localQuestion, serverQuestion, null, null, false);
    if (JSON.stringify(translated) !== JSON.stringify({ ...serverQuestion, visibility: null })) {
      ops.push({ op: 'updateQuestion', sectionId, questionId: serverQuestionId, question: translated });
    }
  }

  for (const localQuestion of localWithoutServerId) {
    ops.push({
      op: 'addQuestion',
      sectionId,
      question: prepareQuestionForAdd(localQuestion),
      __localQuestionId: localQuestion.questionId,
    });
  }

  for (const serverQuestion of [...serverQuestions].reverse()) {
    if (!survivingServerQuestionIds.has(serverQuestion.questionId)) {
      ops.push({ op: 'deleteQuestion', sectionId, questionId: serverQuestion.questionId });
    }
  }

  if (localWithoutServerId.length === 0 && survivingServerQuestionIds.size === serverQuestions.length) {
    const desiredOrder = localWithServerId.map((entry) => entry.serverQuestionId);
    const currentOrder = serverQuestions.map((q) => q.questionId);
    if (desiredOrder.length === currentOrder.length && desiredOrder.some((id, index) => id !== currentOrder[index])) {
      ops.push({ op: 'reorderQuestions', sectionId, questionOrder: desiredOrder });
    }
  }

  return ops;
}

/**
 * The backend ignores/rejects any client-provided questionId/optionId/
 * rowId/columnId on addQuestion payloads (it always assigns its own), so we
 * strip local ids before sending the question body. Keeping this explicit
 * (rather than relying on the backend to silently ignore them) documents
 * the contract and avoids ever depending on local ids surviving a round trip.
 */
export function prepareQuestionForAdd(question) {
  const { questionId, ...rest } = question;
  const clone = JSON.parse(JSON.stringify(rest));
  delete clone.visibility;
  if (Array.isArray(clone.options)) clone.options = clone.options.map(({ optionId, ...option }) => ({ ...option }));
  if (clone.table) {
    clone.table = {
      ...clone.table,
      rows: (clone.table.rows || []).map(({ rowId, ...row }) => ({ ...row })),
      columns: (clone.table.columns || []).map(({ columnId, ...column }) => ({ ...column })),
    };
  }
  if (clone.repeater) {
    clone.repeater = {
      ...clone.repeater,
      columns: (clone.repeater.columns || []).map(({ columnId, ...column }) => ({ ...column })),
    };
  }
  return clone;
}

function translateCondition(condition, localDefinition, serverDefinition, questionIdMap) {
  if (!condition) return null;
  const translated = JSON.parse(JSON.stringify(condition));
  const serverQuestionId = resolveServerId(condition.questionId, questionIdMap) || condition.questionId;
  translated.questionId = serverQuestionId;
  const localController = (localDefinition.sections || []).flatMap((section) => section.questions || []).find((question) => question.questionId === condition.questionId);
  const serverController = (serverDefinition.sections || []).flatMap((section) => section.questions || []).find((question) => question.questionId === serverQuestionId);
  if (localController && serverController && Array.isArray(localController.options)) {
    translated.values = (translated.values || []).map((value) => {
      const localIndex = localController.options.findIndex((option) => option.optionId === value);
      return localIndex >= 0 && serverController.options && serverController.options[localIndex]
        ? serverController.options[localIndex].optionId : value;
    });
  }
  return translated;
}

function translateNested(items, serverItems, idKey, matchNewByIndex = false) {
  const known = new Set((serverItems || []).map((item) => item[idKey]));
  return (items || []).map((item, index) => {
    const clone = { ...item };
    if (!known.has(clone[idKey])) clone[idKey] = matchNewByIndex && serverItems && serverItems[index] ? serverItems[index][idKey] : '';
    return clone;
  });
}

function translateQuestion(localQuestion, serverQuestion, localDefinition, context, includeVisibility) {
  const clone = JSON.parse(JSON.stringify(localQuestion));
  clone.questionId = serverQuestion.questionId;
  if (clone.options) clone.options = translateNested(clone.options, serverQuestion.options, 'optionId', includeVisibility);
  if (clone.table) {
    clone.table.rows = translateNested(clone.table.rows, serverQuestion.table && serverQuestion.table.rows, 'rowId', includeVisibility);
    clone.table.columns = translateNested(clone.table.columns, serverQuestion.table && serverQuestion.table.columns, 'columnId', includeVisibility);
  }
  if (clone.repeater) clone.repeater.columns = translateNested(clone.repeater.columns, serverQuestion.repeater && serverQuestion.repeater.columns, 'columnId', includeVisibility);
  if (includeVisibility) clone.visibility = translateCondition(clone.visibility, localDefinition, context.serverDefinition, context.questionIdMap);
  else clone.visibility = null;
  return clone;
}

export function diffConditionClearOps(serverDefinition, localDefinition, sectionIdMap = {}, questionIdMap = {}) {
  const ops = [];
  for (const localSection of localDefinition.sections || []) {
    const serverSectionId = resolveServerId(localSection.sectionId, sectionIdMap);
    const serverSection = (serverDefinition.sections || []).find((section) => section.sectionId === serverSectionId);
    if (!serverSection) continue;
    for (const localQuestion of localSection.questions || []) {
      const serverQuestionId = resolveServerId(localQuestion.questionId, questionIdMap);
      const serverQuestion = (serverSection.questions || []).find((question) => question.questionId === serverQuestionId);
      const desiredVisibility = translateCondition(localQuestion.visibility, localDefinition, serverDefinition, questionIdMap);
      if (serverQuestion && serverQuestion.visibility && JSON.stringify(desiredVisibility || null) !== JSON.stringify(serverQuestion.visibility)) {
        ops.push({ op: 'updateQuestion', sectionId: serverSectionId, questionId: serverQuestionId, question: { ...serverQuestion, visibility: null } });
      }
    }
    const desiredSectionVisibility = translateCondition(localSection.visibility, localDefinition, serverDefinition, questionIdMap);
    if (serverSection.visibility && JSON.stringify(desiredSectionVisibility || null) !== JSON.stringify(serverSection.visibility)) {
      ops.push({ op: 'setSectionVisibility', sectionId: serverSectionId, visibility: null });
    }
  }
  return ops;
}

export function diffConditionOps(serverDefinition, localDefinition, sectionIdMap = {}, questionIdMap = {}) {
  const questionOps = [];
  const sectionOps = [];
  const context = { serverDefinition, questionIdMap };
  for (const localSection of localDefinition.sections || []) {
    const serverSectionId = resolveServerId(localSection.sectionId, sectionIdMap);
    const serverSection = (serverDefinition.sections || []).find((section) => section.sectionId === serverSectionId);
    if (!serverSection) continue;
    for (const localQuestion of localSection.questions || []) {
      const serverQuestionId = resolveServerId(localQuestion.questionId, questionIdMap);
      const serverQuestion = (serverSection.questions || []).find((question) => question.questionId === serverQuestionId);
      if (!serverQuestion) continue;
      const desired = translateQuestion(localQuestion, serverQuestion, localDefinition, context, true);
      if (JSON.stringify(desired.visibility || null) !== JSON.stringify(serverQuestion.visibility || null)) {
        questionOps.push({ op: 'updateQuestion', sectionId: serverSectionId, questionId: serverQuestionId, question: desired });
      }
    }
    const visibility = translateCondition(localSection.visibility, localDefinition, serverDefinition, questionIdMap);
    if (JSON.stringify(visibility || null) !== JSON.stringify(serverSection.visibility || null)) {
      sectionOps.push({ op: 'setSectionVisibility', sectionId: serverSectionId, visibility });
    }
  }
  return [...questionOps, ...sectionOps];
}
